// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Board-level facts about how two files differ.
 *
 * These used to be checks, and the tool used to render them as a PASS or a FAIL.
 * That verdict answered a question only the old region merge asked — *is every
 * difference inside the agreed area* — and it stopped meaning anything the moment
 * the user became the one deciding which differences to take.
 *
 * So none of this is a verdict now. It is a description: here is what the two
 * files disagree about at board level, stated plainly, so the person choosing
 * knows what they are choosing between. Whether any of it blocks a *merge* is
 * decided against a plan, in ./plan.ts.
 */

import { structuralEqual } from '../sexp/index.ts';
import type { MaybeBBox } from '../model/bbox.ts';
import type { Board } from '../model/board.ts';
import type { DiffResult } from '../diff/diff.ts';
import { normalizeGlobalBlock } from '../diff/normalize.ts';
import { diffFields, type FieldChange } from '../diff/fields.ts';
import type { GlobalBlockId } from '../select/plan.ts';

export type FactId = 'nets' | 'footprints' | 'designators' | 'layers' | 'rules' | 'textvars' | 'version';

export interface FactInfo {
  /** One to three words, shown in the list. */
  label: string;
  /** Full explanation, shown on hover. */
  help: string;
}

export const FACT_INFO: Record<FactId, FactInfo> = {
  nets: {
    label: 'Nets',
    help:
      'Whether both boards declare the same net names. A difference means the schematic drifted or a net ' +
      'was created in layout. Objects reference nets by a file-local index, so anything spliced has its ' +
      'nets re-resolved by name — which only works if the destination has the name.',
  },
  footprints: {
    label: 'Footprints',
    help:
      'Whether both boards carry the same schematic-linked footprints. A difference means the schematic ' +
      'drifted, or footprints were added or deleted in layout.',
  },
  designators: {
    label: 'Designators',
    help:
      'Whether both boards use the same reference designators. A difference usually means the board was ' +
      're-annotated, which desynchronises it from the schematic.',
  },
  layers: {
    label: 'Layer stack',
    help:
      'Whether the layer tables match. If they do not, the two files disagree about what the board ' +
      'physically is, and anything taken from one may draw on a layer the other has never heard of.',
  },
  rules: {
    label: 'Design rules',
    help:
      'Whether design rules, netclasses and the stackup match. These govern the whole board rather than ' +
      'any one object, so they are taken or not taken as a single unit.',
  },
  textvars: {
    label: 'Text variables',
    help:
      'Whether board text variables match. Text drawn from a variable changes meaning when the variable ' +
      'does, so silkscreen and fabrication notes can read differently with no object having changed.',
  },
  version: {
    label: 'KiCad version',
    help:
      'Whether both files came from the same KiCad version. Different versions rewrite whole blocks on ' +
      'save, so differences between them cannot be trusted to be real edits.',
  },
};

export interface FactDetail {
  title: string;
  detail: string;
  objectId?: string;
  bbox?: MaybeBBox;
  fields?: FieldChange[];
}

export interface BoardFact {
  id: FactId;
  label: string;
  help: string;
  /** True when the two files agree. Not a pass — just an answer. */
  same: boolean;
  /** One line, shown whether or not they agree. */
  summary: string;
  /** Itemised differences, for the ones that have them. */
  details: FactDetail[];
  /**
   * The board-level block a user could take from Board B to resolve this,
   * when there is one.
   */
  resolvableBy?: GlobalBlockId;
}

/**
 * Files written by different major KiCad versions are refused outright rather
 * than compared and quietly mis-reported.
 */
export class IncomparableFilesError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IncomparableFilesError';
  }
}

function majorVersion(board: Board): string | undefined {
  const gv = board.generatorVersion;
  if (gv !== undefined && gv !== '') return gv.split('.')[0];
  return board.version;
}

export function assertComparable(a: Board, b: Board): void {
  const ma = majorVersion(a);
  const mb = majorVersion(b);
  if (ma !== undefined && mb !== undefined && ma !== mb) {
    throw new IncomparableFilesError(
      `${a.fileName} was written by KiCad ${a.generatorVersion ?? a.version} and ${b.fileName} by ` +
        `KiCad ${b.generatorVersion ?? b.version}. Comparing files across major KiCad versions is refused: ` +
        `the newer version rewrites blocks wholesale, so every difference would be reported and none could ` +
        `be trusted. Open both files in the same KiCad version, save, and try again.`,
    );
  }
}

export interface CompareOptions {
  /** Skip the major-version refusal. Only for tests. */
  allowVersionMismatch?: boolean;
}

/** Describe how two boards differ at board level. Never throws for ordinary differences. */
export function compareBoards(a: Board, b: Board, diff: DiffResult, options: CompareOptions = {}): BoardFact[] {
  if (!options.allowVersionMismatch) assertComparable(a, b);

  const facts: BoardFact[] = [
    netFact(a, b, diff),
    footprintFact(a, b),
    designatorFact(a, b),
    blockFact('layers', 'layers', a, b, 'layers'),
    blockFact('rules', 'setup', a, b, 'setup'),
    textVarFact(a, b, diff),
    versionFact(a, b),
  ];

  for (const f of facts) {
    f.label = FACT_INFO[f.id].label;
    f.help = FACT_INFO[f.id].help;
  }
  return facts;
}

// --- nets ------------------------------------------------------------------------

function netFact(a: Board, b: Board, diff: DiffResult): BoardFact {
  // Only a board with a net *table* has anything to take. Where nets are named
  // on the objects themselves, offering to take the table offers nothing.
  const resolvable = a.usesNamedNets ? undefined : ('nets' as const);
  const { onlyInA, onlyInB, reindexed } = diff.global.nets;
  const details: FactDetail[] = [];

  for (const name of onlyInA) {
    details.push({
      title: `Net "${name}" exists in ${a.fileName} but not in ${b.fileName}`,
      detail: 'A net disappeared. The schematic drifted, or nets were deleted in layout.',
    });
  }
  for (const name of onlyInB) {
    details.push({
      title: `Net "${name}" exists in ${b.fileName} but not in ${a.fileName}`,
      detail: a.usesNamedNets
        ? 'A net was added. This board names nets on the objects themselves, so taking an object that ' +
          'uses it brings the net across with it.'
        : 'A net was added. Anything taken from Board B that uses it needs Board A to have it too — ' +
          'either take the net table, or leave those objects behind.',
    });
  }

  const note =
    reindexed.length > 0
      ? ` ${reindexed.length} net(s) have the same name at a different index — harmless, because every ` +
        `comparison and splice goes through the name, never the index.`
      : '';

  return {
    id: 'nets',
    label: '',
    help: '',
    same: details.length === 0,
    summary:
      details.length === 0
        ? `${a.allNetNames().size} nets, names identical.${note}`
        : `${onlyInA.length} net(s) only in A, ${onlyInB.length} only in B.`,
    details,
    ...(resolvable ? { resolvableBy: resolvable } : {}),
  };
}

// --- footprints ------------------------------------------------------------------

function footprintFact(a: Board, b: Board): BoardFact {
  const pa = a.footprintPaths();
  const pb = b.footprintPaths();
  const setA = new Set(pa);
  const setB = new Set(pb);
  const details: FactDetail[] = [];

  for (const p of pa) {
    if (setB.has(p)) continue;
    const obj = a.byId.get(`path:${p}`);
    details.push({
      title: `Footprint ${obj?.reference ?? p} is in ${a.fileName} but not in ${b.fileName}`,
      detail: 'A footprint was deleted, or the schematic link was broken.',
      ...(obj ? { objectId: obj.id, bbox: obj.bbox } : {}),
    });
  }
  for (const p of pb) {
    if (setA.has(p)) continue;
    const obj = b.byId.get(`path:${p}`);
    details.push({
      title: `Footprint ${obj?.reference ?? p} is in ${b.fileName} but not in ${a.fileName}`,
      detail: 'A footprint was added with a schematic link Board A does not have.',
      ...(obj ? { objectId: obj.id, bbox: obj.bbox } : {}),
    });
  }

  // Footprints without a `path` cannot be compared this way; say so rather than
  // let their absence read as a clean bill of health.
  const noPathA = a.objectsOfType('footprint').filter((o) => o.path === undefined || o.path === '').length;
  const noPathB = b.objectsOfType('footprint').filter((o) => o.path === undefined || o.path === '').length;
  const note =
    noPathA > 0 || noPathB > 0
      ? ` ${Math.max(noPathA, noPathB)} footprint(s) have no schematic path (mounting holes, fiducials, logos) ` +
        `and are matched by UUID or geometry instead.`
      : '';

  return {
    id: 'footprints',
    label: '',
    help: '',
    same: details.length === 0,
    summary: details.length === 0 ? `${pa.length} linked footprints, identical.${note}` : `${details.length} difference(s).`,
    details,
  };
}

// --- designators -----------------------------------------------------------------

function designatorFact(a: Board, b: Board): BoardFact {
  const ra = a.references();
  const rb = b.references();
  const setA = new Set(ra);
  const setB = new Set(rb);
  const details: FactDetail[] = [];

  for (const r of ra) {
    if (!setB.has(r)) {
      details.push({ title: `${r} is in ${a.fileName} but not in ${b.fileName}`, detail: 'The board may have been re-annotated.' });
    }
  }
  for (const r of rb) {
    if (!setA.has(r)) {
      details.push({ title: `${r} is in ${b.fileName} but not in ${a.fileName}`, detail: 'The board may have been re-annotated.' });
    }
  }

  return {
    id: 'designators',
    label: '',
    help: '',
    same: details.length === 0,
    summary: details.length === 0 ? `${ra.length} references, identical.` : `${details.length} difference(s).`,
    details,
  };
}

// --- global blocks ----------------------------------------------------------------

function blockFact(id: FactId, block: string, a: Board, b: Board, resolvableBy: GlobalBlockId): BoardFact {
  const an = a.globalBlocks.get(block);
  const bn = b.globalBlocks.get(block);
  const details: FactDetail[] = [];

  if (an === undefined && bn === undefined) {
    return { id, label: '', help: '', same: true, summary: `Neither file has a \`${block}\` block.`, details };
  }

  if (an === undefined || bn === undefined) {
    details.push({
      title: `\`${block}\` is present in only one file`,
      detail: `Present in ${an === undefined ? b.fileName : a.fileName} only.`,
    });
  } else {
    const na = normalizeGlobalBlock(an);
    const nb = normalizeGlobalBlock(bn);
    if (!structuralEqual(na, nb)) {
      const fields = diffFields(na, nb, block);
      details.push({
        title: `\`${block}\` differs (${fields.length} field${fields.length === 1 ? '' : 's'})`,
        detail:
          `This block governs the whole board, so it is taken or not taken as a unit. Unless you take ` +
          `it, the merged file keeps ${a.fileName}'s version.`,
        fields,
      });
    }
  }

  return {
    id,
    label: '',
    help: '',
    same: details.length === 0,
    summary: details.length === 0 ? `\`${block}\` identical after normalisation.` : `\`${block}\` differs.`,
    details,
    resolvableBy,
  };
}

function textVarFact(a: Board, b: Board, diff: DiffResult): BoardFact {
  const block = diff.global.blocks.find((x) => x.block === 'properties');
  const changed = block !== undefined && block.status !== 'identical' && block.status !== 'absent';
  const details: FactDetail[] = [];

  if (changed) {
    details.push({
      title: `Board text variables differ (${block.fields.length} field${block.fields.length === 1 ? '' : 's'})`,
      detail:
        'Text drawn from a variable changes meaning when the variable does. Unless you take the ' +
        'variables, the merged file keeps Board A’s values.',
      fields: block.fields,
    });
  }

  return {
    id: 'textvars',
    label: '',
    help: '',
    same: !changed,
    summary: changed ? 'Text variables differ.' : `${a.boardProperties.size} text variable(s), identical.`,
    details,
    resolvableBy: 'properties',
  };
}

// --- version -----------------------------------------------------------------------

function versionFact(a: Board, b: Board): BoardFact {
  const details: FactDetail[] = [];
  if (a.version !== b.version) {
    details.push({
      title: `File format version differs: ${a.version ?? '(none)'} vs ${b.version ?? '(none)'}`,
      detail: 'Some differences below may be format churn rather than edits.',
    });
  }
  if (a.generatorVersion !== b.generatorVersion) {
    details.push({
      title: `Generator version differs: ${a.generatorVersion ?? '(none)'} vs ${b.generatorVersion ?? '(none)'}`,
      detail: 'Re-save both files from the same KiCad version before merging anything.',
    });
  }

  return {
    id: 'version',
    label: '',
    help: '',
    same: details.length === 0,
    summary:
      details.length === 0
        ? `Both written by KiCad ${a.generatorVersion ?? '(unknown)'}, format ${a.version ?? '(unknown)'}.`
        : 'Version mismatch.',
    details,
  };
}
